import { TextArea, type TextAreaProps as AriaTextAreaProps } from "react-aria-components";
import { tv } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

export type TextArea2Props = AriaTextAreaProps & {
  [key: `data-${string}`]: string | undefined;
  className?: string;
  // "display" renders an inert, borderless textarea — for read-only contexts
  // (e.g. note display views). The label stays owned by whichever molecule
  // (e.g. TextField2) composes this atom.
  mode?: "display" | "edit";
};

export function TextArea2({ className, mode = "edit", value, ...props }: TextArea2Props) {
  const { area } = textAreaStyles({ mode });
  const isDisplay = mode === "display";
  const hasFallbackValue = isDisplay && !value;

  return (
    <TextArea
      className={cn(area(), "atom-focus", hasFallbackValue && "tw:text-text-quaternary", className)}
      value={hasFallbackValue ? "—" : value}
      {...{ inert: isDisplay ? "" : undefined }}
      {...props}
    />
  );
}

const textAreaStyles = tv({
  defaultVariants: { mode: "edit" },
  slots: {
    area: cn(
      "input-label-placeholder",
      "tw:rounded-md tw:border tw:border-solid tw:p-2.5",
      "tw:placeholder:text-text-quinary tw:hover:placeholder:text-text-quaternary",
      "tw:disabled:cursor-not-allowed tw:disabled:bg-fill-senary tw:disabled:placeholder:text-text-quinary",
    ),
  },
  variants: {
    mode: {
      display: {
        area: "tw:border-transparent tw:px-0 tw:pointer-events-none",
      },
      edit: {
        area: "tw:border-stroke-tertiary tw:hover:border-stroke-secondary",
      },
    },
  },
});
